import { Link, useSearchParams } from "react-router-dom";
import styled from "@emotion/styled";
import type { SearchInterpretation } from "@clustec/common";
import { useNetworkStore } from "../stores/network";
import { useSearch } from "../api/hooks";
import { PageContainer, PageTitle, Card, Loading } from "../components/ui";
import { interpretationRow } from "../components/Search";
import { abbreviateHex } from "../components/TxTable";
import { theme } from "../lib/theme";
import { media } from "../lib/breakpoints";

/**
 * Everything one query matched.
 *
 * The header dropdown is a quick-jump: one row per reading, each landing on the first match the
 * probe returned. That is the right default and a dead end when the value appears in forty
 * transactions — the other thirty-nine were fetched and then thrown away. This page is the same
 * search asked for more rows, laid out as one section per reading with every match listed under
 * it, so a nullifier that shows up in a dozen txs is a dozen links rather than a lucky one.
 *
 * Two readings deliberately have no list of their own. An address and a calldata value can appear
 * in millions of transactions, which is exactly what the murder board is for; enumerating them
 * here would be a worse copy of a page that already exists, so those sections say their per-role
 * counts and point at it.
 */

/**
 * How many matches per reading to ask for. The endpoint clamps at 100, and a section longer than
 * that is one where the count in the header, not the list, is the useful information.
 */
export const MATCH_LIMIT = 100;

/** Plain-English names for how the server read the query, shown once under the title. */
const QUERY_TYPE_LABELS: Record<string, string> = {
  fieldHex: "a 64-hex field",
  l1Address: "an L1 address",
  selector: "a function selector",
  numeric: "a number",
  label: "label text",
};

/**
 * One match inside a section: the thing that matched, whatever distinguishes it from its
 * neighbours, and where it lives. `to` is nullable for the same reason it is on a dropdown row —
 * a match with no page to land on is still worth showing, just not worth pretending is a link.
 */
export interface SearchMatchRow {
  label: string;
  detail: string;
  to: string | null;
}

function txRow(network: string, txHash: string, detail: string): SearchMatchRow {
  return { label: txHash, detail, to: `/${network}/tx/${txHash}` };
}

/**
 * Every match of one interpretation, in server order (most recent first).
 *
 * This is the routing table for the page and the part that rots silently: a page moves, a kind
 * gains matches server-side, and nothing throws — the section just renders empty or links into a
 * 404. So it is pure, exported, and walked exhaustively, with the single-entity kinds spelled out
 * rather than swept into a `default` so that a kind added to the union fails to compile here.
 */
export function matchRows(
  interpretation: SearchInterpretation,
  network: string,
): SearchMatchRow[] {
  switch (interpretation.kind) {
    case "nullifier":
    case "noteHash":
    case "logField":
      return interpretation.matches.map((m) =>
        txRow(network, m.txHash, `position ${m.position}`),
      );

    case "l2ToL1Message":
      // Which end of the message is interesting depends on which field matched, so both are
      // named; the content is usually the query itself and is left out.
      return interpretation.matches.map((m) =>
        txRow(
          network,
          m.txHash,
          [
            m.sender != null ? `from ${abbreviateHex(m.sender)}` : null,
            m.recipient != null ? `to ${abbreviateHex(m.recipient)}` : null,
          ]
            .filter((part) => part !== null)
            .join(", "),
        ),
      );

    case "publicDataWrite":
      return interpretation.matches.map((m) =>
        txRow(network, m.txHash, `slot ${abbreviateHex(m.leafSlot)}`),
      );

    case "contractClass":
      return interpretation.matches.map((m) =>
        txRow(network, m.txHash, `contract ${abbreviateHex(m.contractAddress)}`),
      );

    case "functionSelector":
      return interpretation.matches.map((m) => txRow(network, m.txHash, ""));

    case "blockProducer":
      return interpretation.blockNumbers.map((n) => ({
        label: `Block ${n}`,
        detail: "",
        to: `/${network}/block/${n}`,
      }));

    case "tx":
    case "block":
    case "address":
    case "calldataValue":
    case "validator":
    case "slot":
    case "epoch":
    case "checkpoint":
    case "label":
    case "rollupContract":
      // One entity apiece: the section header is the row, and its link is the whole answer.
      return [];
  }
}

/**
 * The line under a section that came back full. A list of exactly `limit` rows is a list the
 * server stopped writing, not one that ended — saying so is the difference between "these are the
 * matches" and "these are the first hundred".
 */
export function cappedNotice(matchCount: number, limit: number): string | null {
  return matchCount >= limit ? `Showing the first ${limit} matches.` : null;
}

/**
 * What a section with no list of its own says instead. The counts are already in the header's
 * evidence line; this is the sentence that tells you where the rest of the answer is.
 */
export function sectionHint(interpretation: SearchInterpretation): string {
  switch (interpretation.kind) {
    case "address":
      return "The murder board lists every transaction this address appears in.";
    case "calldataValue":
      return "The murder board lists every transaction carrying this value.";
    case "validator":
      return "Proposals and misses are on the health page.";
    case "rollupContract":
      return "This is the rollup contract of the selected network. There is no page for it.";
    default:
      return "";
  }
}

const Summary = styled.p`
  margin: 0 0 ${theme.spacing.md};
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.textMuted};
`;

const Query = styled.span`
  font-family: "SF Mono", "Fira Code", monospace;
  color: ${theme.colors.text};
  word-break: break-all;
`;

const Section = styled(Card)`
  margin-bottom: ${theme.spacing.md};
`;

const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
`;

/** Same fixed-width badge as the dropdown, so a reading looks the same in both places. */
const Kind = styled.span`
  flex-shrink: 0;
  min-width: 116px;
  font-size: ${theme.fontSize.xs};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${theme.colors.primary};
`;

const HeadLabel = styled.span`
  font-family: "SF Mono", "Fira Code", monospace;
  font-size: ${theme.fontSize.sm};
  word-break: break-all;
`;

const HeadLink = styled(Link)`
  font-family: "SF Mono", "Fira Code", monospace;
  font-size: ${theme.fontSize.sm};
  word-break: break-all;
  color: ${theme.colors.primary};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Evidence = styled.span`
  margin-left: auto;
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};

  ${media.sm} {
    margin-left: 0;
  }
`;

const Matches = styled.ul`
  margin: ${theme.spacing.sm} 0 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid ${theme.colors.border};
`;

const Match = styled.li`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing.sm};
  padding: 4px 0;
  border-bottom: 1px solid ${theme.colors.border};

  ${media.sm} {
    flex-direction: column;
    gap: 0;
  }
`;

const MatchLink = styled(Link)`
  font-family: "SF Mono", "Fira Code", monospace;
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.primary};
  text-decoration: none;
  word-break: break-all;
  &:hover {
    text-decoration: underline;
  }
`;

const MatchLabel = styled.span`
  font-family: "SF Mono", "Fira Code", monospace;
  font-size: ${theme.fontSize.xs};
  word-break: break-all;
`;

const Detail = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};

  ${media.sm} {
    margin-left: 0;
  }
`;

const Note = styled.p`
  margin: ${theme.spacing.sm} 0 0;
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.md};
`;

export function SearchResults() {
  const { selectedNetwork } = useNetworkStore();
  const [searchParams] = useSearchParams();
  // The raw query, prefix hint and all: the server strips `tx:`/`block:`/… itself, and rewriting
  // it here would drop the hint that narrowed the search in the first place.
  const query = searchParams.get("q") ?? "";
  const trimmed = query.trim();

  const { data, isLoading, isError } = useSearch(
    selectedNetwork,
    trimmed,
    trimmed !== "",
    MATCH_LIMIT,
  );

  return (
    <PageContainer>
      <PageTitle>Search</PageTitle>

      {trimmed === "" && <EmptyState>Search for a hash, address, block, slot or epoch.</EmptyState>}

      {trimmed !== "" && data && (
        <Summary>
          <Query>{data.normalizedQuery}</Query> read as{" "}
          {QUERY_TYPE_LABELS[data.queryType] ?? data.queryType} —{" "}
          {data.interpretations.length === 1
            ? "1 reading"
            : `${data.interpretations.length} readings`}
        </Summary>
      )}

      {trimmed !== "" && isLoading && <Loading />}

      {/* A failed request is not a query nobody matched: saying "no matches" for a 500 would send
          someone off looking for a hash they typed correctly. */}
      {trimmed !== "" && isError && (
        <Card>
          <EmptyState>Search is unavailable right now.</EmptyState>
        </Card>
      )}

      {trimmed !== "" && data && data.interpretations.length === 0 && (
        <Card>
          <EmptyState>Nothing on this network matches that.</EmptyState>
        </Card>
      )}

      {data?.interpretations.map((interpretation, i) => {
        const header = interpretationRow(interpretation, selectedNetwork);
        const rows = matchRows(interpretation, selectedNetwork);
        const hint = sectionHint(interpretation);
        const notice = cappedNotice(rows.length, MATCH_LIMIT);
        // A section with a list links from its rows; making the header a link too would point at
        // the first match twice and read as if it were the section's own destination.
        const headerTarget = rows.length === 0 ? header.to : null;
        return (
          <Section key={`${interpretation.kind}-${i}`}>
            <SectionHead>
              <Kind>{header.kind}</Kind>
              {headerTarget != null ? (
                <HeadLink to={headerTarget}>{header.label}</HeadLink>
              ) : (
                <HeadLabel>{header.label}</HeadLabel>
              )}
              {header.evidence !== "" && <Evidence>{header.evidence}</Evidence>}
            </SectionHead>

            {rows.length > 0 && (
              <Matches>
                {rows.map((row, j) => (
                  <Match key={`${row.label}-${j}`}>
                    {row.to != null ? (
                      <MatchLink to={row.to}>{row.label}</MatchLink>
                    ) : (
                      <MatchLabel>{row.label}</MatchLabel>
                    )}
                    {row.detail !== "" && <Detail>{row.detail}</Detail>}
                  </Match>
                ))}
              </Matches>
            )}

            {hint !== "" && <Note>{hint}</Note>}
            {notice != null && <Note>{notice}</Note>}
          </Section>
        );
      })}
    </PageContainer>
  );
}
